import React, { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { removeFromCart, updateQuantity } from "../redux/cartSlice";
import { useNavigate } from "react-router-dom";

/* ─── Design tokens (mirrors Footer / Checkout / Admin) ───── */
const T = {
  rose:       "#c0506e",
  roseDark:   "#8f2d46",
  roseLight:  "#e8839a",
  roseSoft:   "#fdf0f3",
  roseMid:    "#f3c5cf",
  ivory:      "#faf8f5",
  ivoryDark:  "#f0ece6",
  warm100:    "#e8e3dc",
  warm200:    "#d4cfc8",
  warm400:    "#9e9189",
  warm700:    "#3d3530",
  ink:        "#1a1510",
  ffDisplay:  "'Cormorant Garamond', Georgia, serif",
  ffBody:     "'Outfit', system-ui, sans-serif",
  easeLux:    "cubic-bezier(0.16, 1, 0.3, 1)",
  easeSnap:   "cubic-bezier(0.34, 1.56, 0.64, 1)",
};

/* ─── Accent bar ──────────────────────────────────────────── */
const AccentBar = () => (
  <div style={{
    height: 3,
    background: `linear-gradient(90deg,
      transparent 0%, ${T.roseMid} 15%,
      ${T.rose} 50%, ${T.roseMid} 85%, transparent 100%)`,
  }} />
);

/* ─── Qty stepper button ──────────────────────────────────── */
const StepBtn = ({ onClick, disabled, children }) => {
  const [hov, setHov] = React.useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 30, height: 30,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: "none",
        background: hov && !disabled ? T.roseSoft : "transparent",
        color: disabled ? T.warm200 : hov ? T.rose : T.warm700,
        borderRadius: 6,
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 16,
        lineHeight: 1,
        transition: "background 150ms, color 150ms",
        flexShrink: 0,
      }}
    >{children}</button>
  );
};

/* ─── Cart item row ───────────────────────────────────────── */
const CartItem = ({ item, onIncrease, onDecrease, onRemove }) => {
  const [hov, setHov] = React.useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto auto auto",
        alignItems: "center",
        gap: "clamp(0.75rem, 2vw, 1.5rem)",
        padding: "clamp(0.85rem, 2vw, 1.1rem) clamp(0.75rem, 2vw, 1rem)",
        borderRadius: 12,
        background: hov ? T.roseSoft : T.ivory,
        border: `1px solid ${hov ? T.roseMid : T.warm100}`,
        transition: "background 180ms, border-color 180ms, box-shadow 180ms",
        boxShadow: hov ? `0 4px 20px rgba(192,80,110,.08)` : "none",
      }}
    >
      {/* Product info */}
      <div style={{ display: "flex", alignItems: "center", gap: "clamp(0.6rem, 2vw, 1rem)", minWidth: 0 }}>
        <div style={{
          width: "clamp(52px, 8vw, 68px)",
          height: "clamp(52px, 8vw, 68px)",
          borderRadius: 10,
          overflow: "hidden",
          flexShrink: 0,
          border: `1px solid ${T.warm100}`,
        }}>
          <img
            src={item.imageUrl || "/placeholder.jpg"}
            alt={item.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{
            margin: 0,
            fontFamily: T.ffBody,
            fontWeight: 600,
            fontSize: "clamp(13px, 2vw, 15px)",
            color: T.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>{item.name}</p>
          <p style={{
            margin: "3px 0 0",
            fontFamily: T.ffDisplay,
            fontSize: "clamp(13px, 2vw, 14px)",
            color: T.warm400,
            fontWeight: 600,
          }}>GH₵{Number(item.price).toFixed(2)}</p>
        </div>
      </div>

      {/* Qty stepper */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        background: "#fff",
        border: `1.5px solid ${T.warm200}`,
        borderRadius: 8,
        padding: "2px",
        flexShrink: 0,
      }}>
        <StepBtn onClick={onDecrease} disabled={item.quantity <= 1}>−</StepBtn>
        <span style={{
          fontFamily: T.ffBody,
          fontWeight: 600,
          fontSize: 14,
          color: T.ink,
          minWidth: 24,
          textAlign: "center",
        }}>{item.quantity}</span>
        <StepBtn onClick={onIncrease}>+</StepBtn>
      </div>

      {/* Line total */}
      <span style={{
        fontFamily: T.ffDisplay,
        fontWeight: 700,
        fontSize: "clamp(14px, 2.5vw, 16px)",
        color: T.roseDark,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}>GH₵{(item.price * item.quantity).toFixed(2)}</span>

      {/* Remove */}
      <button
        onClick={onRemove}
        title="Remove item"
        style={{
          width: 32, height: 32,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "none",
          border: `1.5px solid ${T.warm200}`,
          borderRadius: 8,
          cursor: "pointer",
          color: T.warm400,
          fontSize: 14,
          transition: "all 180ms",
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = T.roseSoft;
          e.currentTarget.style.borderColor = T.rose;
          e.currentTarget.style.color = T.rose;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "none";
          e.currentTarget.style.borderColor = T.warm200;
          e.currentTarget.style.color = T.warm400;
        }}
      >✕</button>
    </div>
  );
};

/* ─── Summary row ─────────────────────────────────────────── */
const SummaryRow = ({ label, value, bold }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
    <span style={{
      fontFamily: T.ffBody,
      fontSize: bold ? 13 : 13,
      fontWeight: bold ? 600 : 400,
      color: bold ? T.roseDark : T.warm400,
      letterSpacing: bold ? "0.06em" : 0,
      textTransform: bold ? "uppercase" : "none",
    }}>{label}</span>
    <span style={{
      fontFamily: bold ? T.ffDisplay : T.ffBody,
      fontSize: bold ? "clamp(1.3rem, 3vw, 1.55rem)" : 14,
      fontWeight: 700,
      color: bold ? T.roseDark : T.warm700,
    }}>{value}</span>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   CartPage
   ═══════════════════════════════════════════════════════════ */
const CartPage = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const cartItems = useSelector((state) => state.cart.items);

  const getTotalPrice = () =>
    cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0).toFixed(2);

  const increaseQty = useCallback((item) =>
    dispatch(updateQuantity({ id: item.id, quantity: item.quantity + 1 })), [dispatch]);

  const decreaseQty = useCallback((item) => {
    if (item.quantity > 1)
      dispatch(updateQuantity({ id: item.id, quantity: item.quantity - 1 }));
  }, [dispatch]);

  const itemCount = cartItems.reduce((a, i) => a + i.quantity, 0);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Outfit:wght@400;500;600&display=swap');
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cart-checkout-btn:not(:disabled):hover {
          background: ${T.roseDark} !important;
          box-shadow: 0 8px 28px rgba(192,80,110,.32) !important;
          transform: translateY(-2px);
        }
        .cart-checkout-btn { transition: background 220ms, box-shadow 220ms, transform 200ms; }
        .cart-checkout-btn:disabled { opacity: .45; cursor: not-allowed; }
        .cart-back-btn:hover { color: ${T.rose} !important; }
        .cart-back-btn { transition: color 180ms; }
      `}</style>

      <div style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse 70% 50% at 100% 0%, rgba(192,80,110,.05) 0%, transparent 65%),
          radial-gradient(ellipse 50% 40% at 0% 100%, rgba(192,80,110,.03) 0%, transparent 55%),
          ${T.ivoryDark}
        `,
        padding: "clamp(2rem, 6vw, 4.5rem) clamp(1rem, 5vw, 2rem)",
        fontFamily: T.ffBody,
      }}>

        {/* Page header */}
        <div style={{ maxWidth: 1100, margin: "0 auto clamp(1.5rem, 4vw, 2.5rem)" }}>
          <p style={{ margin: "0 0 .35rem", fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: T.rose }}>
            GlossyLuxHair
          </p>
          <h1 style={{ margin: 0, fontFamily: T.ffDisplay, fontSize: "clamp(1.9rem, 5vw, 2.7rem)", fontWeight: 700, color: T.ink, letterSpacing: "-0.02em" }}>
            Shopping Cart
            {itemCount > 0 && (
              <span style={{ marginLeft: 12, fontSize: "clamp(1rem, 3vw, 1.3rem)", fontWeight: 600, color: T.warm400 }}>
                ({itemCount} item{itemCount !== 1 ? "s" : ""})
              </span>
            )}
          </h1>
        </div>

        {/* Main layout */}
        <div style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: cartItems.length === 0 ? "1fr" : "repeat(auto-fit, minmax(min(100%, 380px), 1fr))",
          gap: "clamp(1.25rem, 3vw, 2rem)",
          alignItems: "start",
        }}>

          {/* ── Left — item list ── */}
          <div style={{
            background: "#fff",
            borderRadius: 16,
            border: `1px solid ${T.warm100}`,
            boxShadow: `0 2px 8px rgba(26,21,16,.04), 0 12px 40px rgba(192,80,110,.05)`,
            overflow: "hidden",
            animation: "fadeUp 380ms cubic-bezier(0.16,1,0.3,1) both",
          }}>
            <AccentBar />
            <div style={{ padding: "clamp(1.25rem, 3vw, 2rem)" }}>

              {cartItems.length === 0 ? (
                /* Empty state */
                <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
                  <div style={{ fontSize: 48, marginBottom: "1rem" }}>🛒</div>
                  <p style={{ fontFamily: T.ffDisplay, fontSize: "1.35rem", color: T.warm700, margin: "0 0 .5rem" }}>
                    Your cart is empty
                  </p>
                  <p style={{ fontSize: 14, color: T.warm400, margin: "0 0 1.75rem" }}>
                    Discover our luxury hair collection and add your favourites.
                  </p>
                  <button
                    onClick={() => navigate("/shop")}
                    className="cart-checkout-btn"
                    style={{
                      padding: "0.8rem 2rem",
                      fontFamily: T.ffBody,
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#fff",
                      background: T.rose,
                      border: "none",
                      borderRadius: 10,
                      cursor: "pointer",
                      boxShadow: `0 4px 16px rgba(192,80,110,.22)`,
                    }}
                  >
                    ✦ Start Shopping
                  </button>
                </div>
              ) : (
                <>
                  {/* Column headers — hidden on very small screens via opacity trick */}
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto auto auto",
                    gap: "clamp(0.75rem, 2vw, 1.5rem)",
                    padding: "0 clamp(0.75rem, 2vw, 1rem) 0.6rem",
                    marginBottom: "0.5rem",
                    borderBottom: `1px solid ${T.warm100}`,
                  }}>
                    {["Item", "Qty", "Total", ""].map((h, i) => (
                      <span key={i} style={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: T.warm400,
                        textAlign: i > 1 ? "right" : "left",
                      }}>{h}</span>
                    ))}
                  </div>

                  {/* Items */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                    {cartItems.map((item, idx) => (
                      <div key={item.id} style={{ animation: `fadeUp 320ms ${idx * 55}ms cubic-bezier(0.16,1,0.3,1) both` }}>
                        <CartItem
                          item={item}
                          onIncrease={() => increaseQty(item)}
                          onDecrease={() => decreaseQty(item)}
                          onRemove={() => dispatch(removeFromCart(item.id))}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Back to shop */}
                  <button
                    className="cart-back-btn"
                    onClick={() => navigate("/shop")}
                    style={{
                      marginTop: "1.5rem",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontFamily: T.ffBody,
                      fontSize: 13,
                      fontWeight: 600,
                      color: T.warm400,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: 0,
                    }}
                  >
                    ← Back to Shop
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ── Right — order summary ── (only when cart has items) */}
          {cartItems.length > 0 && (
            <div style={{
              background: "#fff",
              borderRadius: 16,
              border: `1px solid ${T.warm100}`,
              boxShadow: `0 2px 8px rgba(26,21,16,.04), 0 12px 40px rgba(192,80,110,.05)`,
              overflow: "hidden",
              animation: "fadeUp 420ms 80ms cubic-bezier(0.16,1,0.3,1) both",
              position: "sticky",
              top: "1.5rem",
            }}>
              <AccentBar />
              <div style={{ padding: "clamp(1.25rem, 3vw, 1.75rem)" }}>

                <h2 style={{
                  fontFamily: T.ffDisplay,
                  fontSize: "clamp(1.2rem, 3vw, 1.45rem)",
                  fontWeight: 700,
                  color: T.roseDark,
                  letterSpacing: "-0.01em",
                  margin: "0 0 1.25rem",
                }}>Order Summary</h2>

                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1rem" }}>
                  <SummaryRow label="Subtotal" value={`GH₵${getTotalPrice()}`} />
                  <SummaryRow label="Shipping" value="Free" />
                </div>

                <div style={{ height: 1, background: T.warm100, margin: "1rem 0" }} />

                {/* Total pill */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: T.roseSoft,
                  border: `1px solid ${T.roseMid}`,
                  borderRadius: 10,
                  padding: "0.85rem 1rem",
                  marginBottom: "1.5rem",
                }}>
                  <span style={{ fontFamily: T.ffBody, fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: T.roseDark }}>
                    Total
                  </span>
                  <span style={{ fontFamily: T.ffDisplay, fontWeight: 700, fontSize: "clamp(1.3rem, 3vw, 1.6rem)", color: T.roseDark }}>
                    GH₵{getTotalPrice()}
                  </span>
                </div>

                {/* Checkout CTA */}
                <button
                  className="cart-checkout-btn"
                  disabled={cartItems.length === 0}
                  onClick={() => navigate("/checkout")}
                  style={{
                    width: "100%",
                    padding: "1rem 1.5rem",
                    fontFamily: T.ffBody,
                    fontWeight: 600,
                    fontSize: 15,
                    letterSpacing: "0.04em",
                    color: "#fff",
                    background: T.rose,
                    border: "none",
                    borderRadius: 12,
                    cursor: "pointer",
                    boxShadow: `0 4px 20px rgba(192,80,110,.22)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  ✦ Proceed to Checkout
                </button>

                {/* Trust note */}
                <p style={{ margin: "0.85rem 0 0", textAlign: "center", fontSize: 12, color: T.warm400, fontFamily: T.ffBody }}>
                  🔒 Secure checkout · Free shipping
                </p>

              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default CartPage;